import { useContext, useEffect } from 'react'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { TABS } from '../Config'

import { TableContext } from 'Context/Table'
import { BillingModalContext } from '../FormBillingModal/Context'
import { ShippingModalContext } from '../FormShippingModal/Context'

import { useProfileStore } from 'Store/Profile'
import {
	useBillingAddressStore,
	INITIAL_STATE as BILLING_INITIAL_STATE,
} from 'Store/BillingAddress'
import {
	useShippingAddressStore,
	INITIAL_STATE as SHIPPING_INITIAL_STATE,
} from 'Store/ShippingAddress'

import Radio from 'Presentation/Components/Table/Cells/Radio'
import CustomActionCell from 'Presentation/Components/Table/Components/CustomActionCell'

import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import formatThaiTaxID from 'Utils/formatThaiTaxID'
import formatThaiPhoneNumber from 'Utils/formatThaiPhoneNumber'

const VIEW_INDEX = 6

const ViewModel = () => {
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const navigate = useNavigate()
	const profile = useProfileStore(state => state.profile, shallow)
	const {
		updateLoading,
		updateError,
		updatePagination,
		resetState: clearStateTable,
	} = useContext(TableContext)
	const { setOpen: setOpenBillingModal, setFormType: setBillingFormType } =
		useContext(BillingModalContext)
	const { setOpen: setOpenShippingModal, setFormType: setShippingFormType } =
		useContext(ShippingModalContext)

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const {
		billingAddress,
		setSelectedBillingAddress,
		getAllBillingAddress,
		activateBillingAddress,
		deleteBillingAddress,
	} = useBillingAddressStore(
		state => ({
			billingAddress: state.data,
			setSelectedBillingAddress: state.setSelected,
			getAllBillingAddress: state.getAll,
			createBillingAddress: state.create,
			activateBillingAddress: state.activate,
			deleteBillingAddress: state.delete,
		}),
		shallow
	)
	const {
		shippingAddress,
		setSelectedShippingAddress,
		getAllShippingAddress,
		activateShippingAddress,
		deleteShippingAddress,
	} = useShippingAddressStore(
		state => ({
			shippingAddress: state.data,
			setSelectedShippingAddress: state.setSelected,
			getAllShippingAddress: state.getAll,
			activateShippingAddress: state.activate,
			deleteShippingAddress: state.delete,
		}),
		shallow
	)

	const fetchData = async () => {
		updateLoading(true)
		const { success: sBill, data: dBill } = await getAllBillingAddress()
		const { success: sShip, data: dShip } = await getAllShippingAddress()

		if (!sBill) {
			updateError(true)
			toast.error(dBill)
		} else {
			updateError(false)
		}
		if (!sShip) {
			updateError(true)
			toast.error(dShip)
		} else {
			updateError(false)
		}

		updateLoading(false)
	}

	const setupTable = () => {
		updateError(false)
		updatePagination(false)
	}

	const handleOpenBillingModal = () => {
		setBillingFormType('add')
		setSelectedBillingAddress(BILLING_INITIAL_STATE)
		setOpenBillingModal(true)
	}

	const handleOpenShippingModal = () => {
		setShippingFormType('add')
		setSelectedShippingAddress(SHIPPING_INITIAL_STATE)
		setOpenShippingModal(true)
	}

	const onCheckActiveBillingAddress = async (itemId: string, active: boolean) => {
		if (billingAddress.length >= 1) {
			const { success, data } = await activateBillingAddress(parseInt(itemId), active ? 1 : 0)
			if (!success) {
				toast.error(data)
			} else {
				toast.success('เปลี่ยนสถานะที่อยู่สำเร็จ')
				getAllBillingAddress()
			}
		}
	}

	const onCheckActiveShippingAddress = async (itemId: string, active: boolean) => {
		if (shippingAddress.length >= 1) {
			const { success, data } = await activateShippingAddress(parseInt(itemId), active ? 1 : 0)
			if (!success) {
				toast.error(data)
			} else {
				toast.success('เปลี่ยนสถานะที่อยู่สำเร็จ')
				getAllShippingAddress()
			}
		}
	}

	const editBilling = (itemId: string) => {
		setBillingFormType('edit')
		const finded = billingAddress.find(item => item.id === itemId)
		if (finded) {
			setSelectedBillingAddress(finded)
			setOpenBillingModal(true)
		} else {
			toast.error('ไม่พบที่อยู่ออกใบกำกับภาษี')
		}
	}

	const deleteBilling = async (itemId: number) => {
		const { success, data } = await deleteBillingAddress(itemId)
		if (!success) {
			toast.error(data)
		} else {
			toast.success('ลบที่อยู่ออกใบกำกับภาษีสำเร็จ')
			getAllBillingAddress()
		}
	}

	const askDeleteBilling = (itemId: number) => {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this address!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Confirm',
			cancelButtonText: 'Cancel',
			customClass: {
				confirmButton: 'btn btn-kumopack-primary-400 text-kumopack-base-white',
				cancelButton: 'btn btn-active-light',
			},
			buttonsStyling: false,
		}).then(async result => {
			if (result.isConfirmed) {
				await deleteBilling(itemId)
			}
		})
	}

	const editShipping = (id: string) => {
		setShippingFormType('edit')
		const finded = shippingAddress.find(item => item.id === id)
		if (finded) {
			setSelectedShippingAddress(finded)
			setOpenShippingModal(true)
		} else {
			toast.error('ไม่พบที่อยู่จัดส่งสินค้า')
		}
	}

	const deleteShipping = async (itemId: number) => {
		const { success, data } = await deleteShippingAddress(itemId)
		if (!success) {
			toast.error(data)
		} else {
			toast.success('ลบที่อยู่จัดส่งสินค้าสำเร็จ')
			getAllShippingAddress()
		}
	}

	const askDeleteShipping = (itemId: number) => {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this address!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Confirm',
			cancelButtonText: 'Cancel',
			customClass: {
				confirmButton: 'btn btn-kumopack-primary-400 text-kumopack-base-white',
				cancelButton: 'btn btn-active-light',
			},
			buttonsStyling: false,
		}).then(async result => {
			if (result.isConfirmed) {
				await deleteShipping(itemId)
			}
		})
	}

	/////////////
	// Columns //
	/////////////

	const BILLING_ADDRESS_COLUMNS: any[] = [
		{
			Header: 'ชื่อเรียกที่อยู่',
			accessor: 'title',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ?? '-'}</div>
			),
		},
		{
			Header: 'Tax ID',
			accessor: 'taxId',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ? formatThaiTaxID(value) : '-'}</div>
			),
		},
		{
			Header: 'หมายเลขโทรศัพท์',
			accessor: 'receiverPhone',
			minWidth: 150,
			width: 150,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ? formatThaiPhoneNumber(value) : '-'}</div>
			),
		},
		{
			Header: 'ที่อยู่',
			accessor: 'address',
			minWidth: 250,
			width: 250,
			Cell: ({ row }: { row: any }) => (
				<div className="text-kumopack-grey-600">
					{`${row.original.tumbon?.zipCode ?? ''} ${row.original.tumbon?.nameTh ?? ''} ${
						row.original.amphure?.nameTh ?? ''
					}
				${row.original.province?.nameTh ?? ''}
				`}
				</div>
			),
		},
		{
			Header: 'ใช้งานอยู่',
			accessor: 'isActive',
			headerRight: true,
			minWidth: 100,
			width: 100,
			Cell: ({ row, value }: { row: any; value: any }) => (
				<Radio
					value={value}
					onChange={e => onCheckActiveBillingAddress(row.original.id, e.target.checked)}
				/>
			),
			disableSortBy: true,
		},
		{
			Header: '',
			accessor: 'action',
			width: 50,
			minWidth: 50,
			Cell: ({ row }: any) => (
				<CustomActionCell
					hideView
					id={row.original.id}
					onClickEdit={editBilling}
					onClickDelete={askDeleteBilling}
				/>
			),
			disableSortBy: true,
		},
	]

	const SHIPPING_ADDRESS_COLUMNS: any[] = [
		{
			Header: 'ชื่อเรียกที่อยู่',
			accessor: 'title',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ?? '-'}</div>
			),
		},
		{
			Header: 'ชื่อ-นามสกุล',
			accessor: 'receiverFullname',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ?? '-'}</div>
			),
		},
		{
			Header: 'หมายเลขโทรศัพท์',
			accessor: 'receiverPhone',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: { value: any }) => (
				<div className="text-kumopack-grey-600">{value ? formatThaiPhoneNumber(value) : '-'}</div>
			),
		},
		{
			Header: 'ที่อยู่',
			accessor: 'address',
			minWidth: 250,
			width: 250,
			Cell: ({ row }: { row: any }) => (
				<div className="text-kumopack-grey-600">
					{`${row.original.tumbon?.zipCode ?? ''} ${row.original.tumbon?.nameTh ?? ''} ${
						row.original.amphure?.nameTh ?? ''
					}
				${row.original.province?.nameTh ?? ''}
				`}
				</div>
			),
		},
		{
			Header: 'ใช้งานอยู่',
			accessor: 'isActive',
			headerRight: true,
			minWidth: 100,
			width: 100,
			Cell: ({ row, value }: { row: any; value: any }) => (
				<Radio
					value={value}
					onChange={e => onCheckActiveShippingAddress(row.original.id, e.target.checked)}
				/>
			),
			disableSortBy: true,
		},
		{
			Header: '',
			accessor: 'action',
			width: 50,
			minWidth: 50,
			Cell: ({ row }: any) => (
				<CustomActionCell
					hideView
					id={row.original.id}
					onClickEdit={editShipping}
					onClickDelete={askDeleteShipping}
				/>
			),
			disableSortBy: true,
		},
	]

	// Setup Table
	useEffect(() => {
		setupTable()
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Unmount Handler
	useEffect(() => {
		return () => {
			clearStateTable()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		companyName: profile?.companyName,
		currentTab: VIEW_INDEX,
		onChangeTab,
		BILLING_ADDRESS_COLUMNS,
		SHIPPING_ADDRESS_COLUMNS,
		billingAddress,
		shippingAddress,
		handleOpenBillingModal,
		handleOpenShippingModal,
	}
}

export default ViewModel
